import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, auth, inputCls, Field, Modal, PageHead, PrimaryBtn, errMsg } from './finance/shared';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import Icon from '../../components/dashboard/DashboardIcons';

export default function BoardPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [toast, setToast] = useState(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/board-members`, auth()); setMembers(res.data?.members || []); } catch { setMembers([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openForm = (m) => {
    setEditing(m || null);
    setForm(m ? { ...m } : { name: '', position: '', description: '', email: '', phone: '', experience: '', image_url: '', order: members.length + 1 });
    setErr(null); setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!form.name?.trim() || !form.position?.trim()) { setErr('الاسم والمنصب حقلان مطلوبان'); return; }
    const payload = { name: form.name.trim(), position: form.position.trim(), description: form.description || null, email: form.email || null, phone: form.phone || null, experience: form.experience || null, image_url: form.image_url?.trim() || null, order: Number(form.order) || 0 };
    try {
      if (editing) await axios.put(`${API}/board-members/${editing.id}`, payload, auth());
      else await axios.post(`${API}/board-members`, payload, auth());
      setModal(false); notify('تم الحفظ بنجاح'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/board-members/${confirmDel.id}`, auth()); notify('تم حذف العضو'); setConfirmDel(null); load(); }
    catch (e2) { notify(errMsg(e2, 'تعذر الحذف')); setConfirmDel(null); }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4" data-testid="board-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <PageHead title="مجلس الإدارة" desc="أعضاء مجلس إدارة المنشأة"
        action={<PrimaryBtn onClick={() => openForm(null)} data-testid="board-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة عضو</PrimaryBtn>} />

      {loading ? <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map((i) => <div key={i} className="h-36 animate-pulse rounded-xl bg-slate-100" />)}</div>
        : members.length === 0 ? <div className="rounded-xl border border-slate-200 bg-white p-8"><EmptyState icon="building" text="لا يوجد أعضاء مسجلون بعد" testId="board-empty" /></div>
        : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {members.map((m) => (
              <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid="board-member-card">
                <div className="flex items-center gap-3">
                  {m.image_url ? <img src={m.image_url} alt="" className="h-12 w-12 rounded-full bg-slate-100 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    : <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#16233b]/10 text-sm font-bold text-[#16233b]">{(m.name || '؟').slice(0, 2)}</div>}
                  <div className="min-w-0">
                    <div className="truncate text-sm font-bold text-slate-900">{m.name}</div>
                    <div className="text-xs font-semibold text-teal-700">{m.position}</div>
                  </div>
                </div>
                {m.description && <p className="mt-2 line-clamp-2 text-xs text-slate-500">{m.description}</p>}
                <div className="mt-2 space-y-0.5 text-xs text-slate-500">
                  {m.phone && <div dir="ltr" className="text-right">{m.phone}</div>}
                  {m.email && <div dir="ltr" className="text-right">{m.email}</div>}
                </div>
                <div className="mt-3 flex justify-end gap-1 border-t border-slate-50 pt-2">
                  <button onClick={() => openForm(m)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-teal-700" data-testid="board-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                  <button onClick={() => setConfirmDel(m)} className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="board-del-btn"><Icon name="trash" className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Modal open={modal} title={editing ? 'تعديل العضو' : 'إضافة عضو مجلس إدارة'} onClose={() => setModal(false)} wide>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="board-form-error">{err}</div>}
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="الاسم" required><input className={inputCls} value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="board-input-name" /></Field>
          <Field label="المنصب" required><input className={inputCls} value={form.position || ''} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="board-input-position" /></Field>
          <Field label="الجوال"><input dir="ltr" className={inputCls} value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="البريد الإلكتروني"><input dir="ltr" type="email" className={inputCls} value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="الخبرات"><input className={inputCls} value={form.experience || ''} onChange={(e) => setForm({ ...form, experience: e.target.value })} /></Field>
          <Field label="الترتيب"><input type="number" min="0" className={inputCls} value={form.order ?? ''} onChange={(e) => setForm({ ...form, order: e.target.value })} /></Field>
          <div className="col-span-full"><Field label="رابط الصورة"><input dir="ltr" placeholder="https://..." className={inputCls} value={form.image_url || ''} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></Field></div>
          <div className="col-span-full"><Field label="نبذة"><textarea rows={2} className={inputCls} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field></div>
          <div className="col-span-full flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="board-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmDel)} title="حذف العضو" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">حذف «{confirmDel?.name}» من مجلس الإدارة؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="board-confirm-del">حذف</button>
        </div>
      </Modal>
    </div>
  );
}
